import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Drawer, Form, Select } from 'antd';
import { FormHelpers } from 'ontraccr-common';
import { getContactAddressBooks } from 'ontraccr-common/lib/Contacts';
import DrawerSubmitFooter from '../common/containers/DrawerSubmitFooter';
import FormSelector from '../forms/FormWorkflows/selectors/FormSelector';
import {
  searchForms,
} from './state/search.actions';
import QuerySelector from './QuerySelector';
import { getIdMap } from '../helpers/helpers';
import ConditionalRenderingRow from '../forms/FormBuilder/FormFields/ConditionalRenderingRow';
import { getTemplateDetails } from '../forms/state/forms.actions';
import CustomFilterRuleInput from './CustomFilterRuleInput';
import { getSearchDrawerState, validateNewValue } from './searchHelpers';
import { filterOptions, formNativeOptions } from './search.constants';
import useProjectCostcodesFetch from '../costcodes/useProjectCostcodesFetch';
import useProjectOrGlobalCostcodes from '../costcodes/useProjectOrGlobalCostcodes';

export default function FormsSearchDrawer({ visible, onClose }) {
  const dispatch = useDispatch();

  const [selectedQuery, setSelectedQuery] = useState();
  const [ourQuery, setOurQuery] = useState({});
  const [filterType, setFilterType] = useState('and');
  const [customFilterRule, setCustomFilterRule] = useState(null);

  const searchQueryMap = useSelector((state) => state.search.formQueries);
  const reduxSelectedQuery = useSelector((state) => state.search.selectedQuery);
  const reduxCurrentQuery = useSelector((state) => state.search.currentQuery);
  const templates = useSelector((state) => state.forms.templates);
  const customers = useSelector((state) => state.customers.customers);
  const projects = useSelector((state) => state.projects.projects);
  const users = useSelector((state) => state.users.users);
  const phases = useSelector((state) => state.costcodes.phases);
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);
  const equipment = useSelector((state) => state.equipment.equipment);
  const vendors = useSelector((state) => state.vendors.vendors);
  const formTemplates = useSelector((state) => state.forms.templates);
  const labels = useSelector((state) => state.labels);
  const buckets = useSelector((state) => state.buckets.buckets);
  const scheduleCategories = useSelector((state) => state.schedule.categories);
  const globalAddressBooks = useSelector((state) => state.contacts.globalAddressBooks);
  const formStatuses = useSelector((state) => state.forms.statuses);
  const materials = useSelector((state) => state.materials.materials);

  const customerList = useMemo(() => Object.values(customers), [customers]);
  const vendorList = useMemo(() => Object.values(vendors), [vendors]);
  const formTemplateList = useMemo(() => Object.values(formTemplates), [formTemplates]);
  const formStatusList = useMemo(() => Object.values(formStatuses), [formStatuses]);
  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);

  const materialsList = useMemo(() => Object.values(materials ?? {}), [materials]);

  const contactAddressBooks = useMemo(() => (
    getContactAddressBooks(globalAddressBooks)
  ), [globalAddressBooks]);

  const costcodeProjectIds = useMemo(() => (
    visible ? projects.map(({ id }) => id) : []
  ), [visible, projects]);
  const divisionIds = useMemo(() => Array.from(selectedDivisions ?? []), [selectedDivisions]);

  const { projectCostcodes } = useProjectCostcodesFetch({ projectIds: costcodeProjectIds });
  const { costcodes: globalCostcodes } = useProjectOrGlobalCostcodes({
    projectId: null,
    divisionIds,
    visible,
  });

  const costcodes = useMemo(() => (
    [...projectCostcodes, ...globalCostcodes]
  ), [projectCostcodes, globalCostcodes]);

  const onSelectQuery = (id, newQuery) => {
    if (id === selectedQuery?.id) return;
    const q = newQuery || searchQueryMap[id];
    setSelectedQuery(q);
    setOurQuery(q?.query || {});
    setFilterType(q?.filterType || 'and');
    setCustomFilterRule(q?.customFilterRule || null);
  };

  const onFormulaChange = useCallback((newFormulas) => {
    setOurQuery({
      ...ourQuery,
      formulas: newFormulas,
    });
  }, [ourQuery]);

  const onTemplateChange = useCallback((newSelected) => {
    if (ourQuery?.templateId !== newSelected) {
      setOurQuery({
        ...ourQuery,
        templateId: newSelected,
        formulas: ourQuery?.formulas?.filter((formula) => !formula.field?.startsWith?.('field-')) || [],
      });
    }
  }, [ourQuery]);

  const analyticsFieldIds = useMemo(() => (
    (ourQuery?.formulas ?? [])
      .filter((f) => f.analytics)
      .map((f) => f.field)
  ), [ourQuery?.formulas]);

  const onSubmit = useCallback(() => {
    const prom = dispatch(searchForms({
      query: ourQuery,
      selectedQuery,
      filterType,
      analyticsFieldIds,
    }));
    onClose(prom);
  }, [onClose, ourQuery, selectedQuery, filterType, analyticsFieldIds]);

  useEffect(() => {
    if (!visible) return;

    const drawerState = getSearchDrawerState('forms', reduxCurrentQuery, reduxSelectedQuery);
    if (!drawerState) {
      setSelectedQuery(undefined);
      setOurQuery({});
      setFilterType('and');
      setCustomFilterRule(null);
      return;
    }

    setOurQuery(drawerState.ourQuery);
    setFilterType(drawerState.filterType);
    setCustomFilterRule(drawerState.customFilterRule);
    setSelectedQuery(drawerState.selectedQuery);
  }, [visible, reduxCurrentQuery, reduxSelectedQuery]);

  useEffect(() => {
    const { templateId } = ourQuery ?? {};
    const getTemplate = async () => {
      await dispatch(getTemplateDetails(templateId));
    };
    if (templateId) {
      getTemplate();
    }
  }, [ourQuery]);

  const ourTemplate = useMemo(() => {
    const { templateId } = ourQuery ?? {};
    const {
      [templateId]: targetForm = {},
    } = templates;
    return targetForm;
  }, [ourQuery, templates]);

  const FormulaWrapper = useCallback(({ index, children }) => (
    <div style={{ marginBottom: 10, border: '1px solid #ccc', padding: 10 }}>
      <b>{index + 1}</b>
      {children}
    </div>
  ), []);

  const canSubmit = useMemo(() => {
    if ((ourQuery?.formulas ?? []).some(FormHelpers.isIncompleteSearchFormula)) return false;
    if (filterType === 'custom') {
      return validateNewValue(customFilterRule, ourQuery?.formulas).isValid;
    }
    return !!filterType && ourQuery?.formulas?.length > 0;
  }, [filterType, ourQuery, customFilterRule]);

  return (
    <Drawer
      title="Search Forms"
      visible={visible}
      onClose={onClose}
      width={1100}
    >
      <div
        style={{
          position: 'absolute',
          top: '10px',
          left: 'calc(100% - 950px)',
        }}
      >
        <QuerySelector
          searchQueryMap={searchQueryMap}
          currentQuery={ourQuery}
          selectedQuery={selectedQuery?.id}
          onSelectQuery={onSelectQuery}
          type="forms"
          filterType={filterType}
          customFilterRule={customFilterRule}
        />
      </div>
      <Col style={{ height: 'calc(100% - 30px)', width: '100%', overflowY: 'scroll' }}>
        <FormSelector
          onChange={onTemplateChange}
          selected={ourQuery?.templateId}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <span>Include data if it matches:</span>
          <Select
            options={filterOptions}
            placeholder="Select a filter type"
            onChange={setFilterType}
            value={filterType}
          />
        </div>
        {filterType === 'custom' && (
          <Form.Item
            label="Custom filter rules"
            name="custom"
          >
            <CustomFilterRuleInput
              ruleValue={customFilterRule}
              onChange={(newValue) => {
                setCustomFilterRule(newValue);
              }}
              formulas={ourQuery?.formulas}
            />
          </Form.Item>
        )}
        <ConditionalRenderingRow
          onFormulaChange={onFormulaChange}
          conditionalRenderingFormula={ourQuery?.formulas}
          sections={ourTemplate?.formData?.sections}
          id="search"
          customers={customerList}
          projects={projects}
          users={users}
          costcodes={costcodes}
          phases={phases}
          projectIdMap={projectIdMap}
          vendors={vendorList}
          equipment={equipment}
          formTemplates={formTemplateList}
          labels={labels}
          contactAddressBooks={contactAddressBooks}
          buckets={buckets}
          scheduleCategories={scheduleCategories}
          materials={materialsList}
          showType={false}
          showCheckbox={false}
          formStatuses={formStatusList}
          additionalFields={formNativeOptions}
          hasConditionalRendering
          FormulaWrapperComponent={FormulaWrapper}
          isAdvancedSearch
        />
      </Col>
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmit}
        canSubmit={canSubmit}
      />
    </Drawer>
  );
}

FormsSearchDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  visible: PropTypes.bool,
};

FormsSearchDrawer.defaultProps = {
  visible: false,
};
